/**
 * HR-pagina "Verlof afsluiten" (Spoor 2, Verlof correctie — Story 4.2)
 *
 * Eén scherm met alles wat vóór de maandafsluiting nog moet. De VOLGORDE van
 * de blokken is de boodschap: ① *wacht op de loonadministratie* staat bovenaan
 * omdat alleen dát blok een verschil met de loonstrook veroorzaakt, ② is een
 * gemiste kans en ③ is bestaand handwerk. Wie die volgorde omgooit, laat HR
 * beginnen bij het minst dringende.
 *
 * Het vierde blok staat dáár ónder en betekent iets anders: calamiteiten- en
 * zorgverlof gaat met opzet nooit naar de loonadministratie, dus daar ging
 * niets mis. Vandaar: onderaan, geen actieknop, geen waarschuwingsteken, geen
 * fouttint, en een eigen lege-blok-tekst — *"Alles doorgezet."* zou daar
 * aantoonbaar onwaar zijn.
 *
 * DEZE PAGINA DEELT NIETS ZELF IN. Alle vier de groepen komen uit de gedeelde
 * classificatie op de server (story 4.1). Een `if (syncStatus === …)` hier is
 * een fout, geen optimalisatie — ook voor het vierde blok.
 */

import { useState } from 'react';
import { Layout } from '../../components/Layout';
import { LeaveCorrectionSheet } from '../verlof/LeaveCorrectionSheet';
import { formatNlUren } from '../verlof/urenFormat';
import {
  useVerlofAfsluiten,
  useVerlofAfsluitenLinkKenmerk,
  useVerlofAfsluitenRetrySync,
  useVerlofKoppelactie,
} from './hooks/useVerlofAfsluiten';
import { vandaagInAmsterdam } from '@functieloonhuis/shared';
import type {
  OwnLeaveHistoryEntry,
  VerlofAandachtRegel,
  VerlofKoppelReden,
} from '@functieloonhuis/shared';

/**
 * UX-DR14 — de reden dat koppelen niet lukte, per code.
 *
 * De eerste drie staan letterlijk in de microcopy-tabel. Voor de laatste twee
 * zwijgt die tabel; ze zijn hier in dezelfde toon geschreven en horen bij de
 * eerstvolgende gelegenheid aan de tabel te worden toegevoegd. De pagina leidt
 * nooit iets af uit deze tekst — of er een knop komt volgt uit
 * `redenIsOpnieuwTeProberen` van de server.
 */
const REDEN_TEKST: Record<VerlofKoppelReden, string> = {
  geen_overeenkomende_regel: 'Geen overeenkomende regel gevonden',
  meerdere_gelijke_regels: 'Meerdere gelijke regels gevonden',
  loonadministratie_onbereikbaar: 'Loonadministratie niet bereikbaar',
  waarden_gewijzigd_na_boeking: 'Waarden zijn na de boeking gewijzigd',
  geboekt_zonder_kenmerk: 'Geboekt zonder kenmerk in de loonadministratie',
};

/** UX-DR14: "12 aug" — dag + korte maandnaam, geen jaartal. */
function formatKorteDatum(iso: string): string {
  const [jaar, maand, dag] = iso.split('-').map(Number);
  const datum = new Date(Date.UTC(jaar ?? 1970, (maand ?? 1) - 1, dag ?? 1));
  return datum.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** "5 augustus 2026" — voor de regel bij de koppelactie. */
function formatVolledigeDatum(iso: string): string {
  const [jaar, maand, dag] = iso.split('-').map(Number);
  const datum = new Date(Date.UTC(jaar ?? 1970, (maand ?? 1) - 1, dag ?? 1));
  return datum.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** De maandnaam van de laatste verlofdag, klein geschreven: `⚠ juli`. */
function maandNaamVan(iso: string): string {
  const [jaar, maand, dag] = iso.split('-').map(Number);
  const datum = new Date(Date.UTC(jaar ?? 1970, (maand ?? 1) - 1, dag ?? 1));
  return datum.toLocaleDateString('nl-NL', { month: 'long', timeZone: 'UTC' });
}

interface AfsluitBlokProps {
  titel: string;
  ondertitel: string;
  testhaak: string;
  aantal: number;
  /**
   * AC-6: *"Alles doorgezet."* is de regel voor de drie bovenste blokken, maar
   * bij een blok waar met opzet nooit iets doorgezet wordt is die zin
   * aantoonbaar onwaar. Daarom een prop met de bestaande zin als
   * standaardwaarde: de regel blijft op één plek en gaat niet liegen.
   */
  legeTekst?: string;
  /** Het vinkje betekent op deze pagina *doorgezet* — blok ④ krijgt het niet. */
  vinkjeBijLeeg?: boolean;
  /** Alleen blok ④ (AC-13): waaróm dit verlof hier staat. */
  uitleg?: string;
  /** Alleen blok ④ (§10.9): *Naam · Datum · Uren*. */
  kolomkoppen?: string[];
  children?: React.ReactNode;
}

function AfsluitBlok({
  titel,
  ondertitel,
  testhaak,
  aantal,
  legeTekst = 'Alles doorgezet.',
  vinkjeBijLeeg = true,
  uitleg,
  kolomkoppen,
  children,
}: AfsluitBlokProps) {
  return (
    <section
      data-testid={testhaak}
      aria-label={`${titel}, ${aantal}`}
      className="mb-6 rounded-lg border border-[#E2E8F0] bg-white p-4"
    >
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold text-[#1E293B]">{titel}</h2>
        <span className="inline-block rounded-full bg-[#54949E]/10 px-2 py-0.5 text-xs font-medium text-[#54949E]">
          {aantal}
        </span>
      </div>
      <p className="mt-1 text-sm text-[#64748B]">{ondertitel}</p>
      {uitleg && <p className="mt-1 text-sm text-[#64748B]">{uitleg}</p>}

      {aantal === 0 ? (
        <p className="mt-3 text-sm text-[#1E293B]">
          {vinkjeBijLeeg && (
            <span aria-hidden="true" style={{ color: '#5C7A00' }}>
              ✓
            </span>
          )}
          {vinkjeBijLeeg ? ' ' : ''}
          {legeTekst}
        </p>
      ) : (
        <>
          {kolomkoppen && (
            <div className="mt-3 flex gap-6 text-xs font-medium uppercase tracking-wide text-[#64748B]">
              {kolomkoppen.map((kop) => (
                <span key={kop}>{kop}</span>
              ))}
            </div>
          )}
          <ul className="mt-2 divide-y divide-[#E2E8F0]">{children}</ul>
        </>
      )}
    </section>
  );
}

interface AandachtRijProps {
  regel: VerlofAandachtRegel;
  /** De reden onder de gegevensregel; `null` bij een blok dat er geen kent. */
  reden?: string | null;
  /** Draagt dit blok de `⚠ maand`-markering? Blok ④ nooit (AC-14). */
  toonMarkering?: boolean;
  actie?: React.ReactNode;
  fout?: string | null;
}

function AandachtRij({ regel, reden, toonMarkering = true, actie, fout }: AandachtRijProps) {
  return (
    <li className="py-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-[#1E293B]">
            <span>{regel.employeeNaam}</span>
            <span aria-hidden="true"> · </span>
            <span>{formatKorteDatum(regel.startDate)}</span>
            <span aria-hidden="true"> · </span>
            <span>{formatNlUren(regel.hours)} uur</span>
            {toonMarkering && regel.uitEerdereMaand && (
              // AC-3: de waarschuwing staat er als WOORD, niet alleen als
              // teken of tint — anders lijkt het werk van deze maand en blijft
              // juli eeuwig hangen.
              <span className="ml-2 text-[#64748B]">
                <span aria-hidden="true">⚠</span> {maandNaamVan(regel.endDate)}
              </span>
            )}
          </p>
          {reden && <p className="mt-0.5 text-xs text-[#64748B]">{reden}</p>}
        </div>
        {actie}
      </div>
      {fout && (
        <p role="alert" className="mt-1 text-xs text-[#D64545]">
          {fout}
        </p>
      )}
    </li>
  );
}

/** De correctie-sheet uit story 2.4 verwacht een `OwnLeaveHistoryEntry`. */
function alsHistorieRegel(regel: VerlofAandachtRegel): OwnLeaveHistoryEntry {
  return {
    id: regel.id,
    leaveType: 0,
    leaveTypeName: regel.leaveTypeName,
    startDate: regel.startDate,
    endDate: regel.endDate,
    startPart: 'hele_dag',
    endPart: 'hele_dag',
    hours: regel.hours,
    description: null,
    requestStatus: regel.requestStatus,
    decisionReason: null,
    syncStatus: regel.syncStatus,
    submittedAt: '',
    startTime: regel.startTime,
    endTime: regel.endTime,
    correctieTermijnEinde: regel.definitiefOp,
    correctieTermijnVerstreken: false,
  } as OwnLeaveHistoryEntry;
}

export function VerlofAfsluitenPage() {
  const [maand, setMaand] = useState<string>(() => vandaagInAmsterdam().slice(0, 7));
  const [regelFouten, setRegelFouten] = useState<Record<number, string>>({});
  const [correctieDoel, setCorrectieDoel] = useState<VerlofAandachtRegel | null>(null);

  const { data, isLoading, isError } = useVerlofAfsluiten(maand);
  const opnieuwProberen = useVerlofAfsluitenRetrySync();
  const opnieuwKoppelen = useVerlofAfsluitenLinkKenmerk();
  const koppelactieStarten = useVerlofKoppelactie();

  const indeling = data?.data.indeling;
  const wacht = indeling?.wachtOpLoonadministratie ?? [];
  const onbevestigd = indeling?.urenNietBevestigd ?? [];
  const ongekoppeld = indeling?.nietGekoppeld ?? [];
  const buiten = indeling?.buitenDeLoonadministratie ?? [];
  const koppelactie = data?.data.koppelactie ?? null;
  const maandIsAfgesloten = data?.data.maandIsAfgesloten ?? false;

  // AC-14: het vierde blok telt hier NIET in mee. Een tien in dat blok is geen
  // tien problemen, en optellen zou HR werk toeschrijven dat er niet is.
  const totaalOpenstaand = wacht.length + onbevestigd.length + ongekoppeld.length;

  function onthoudFout(id: number, boodschap: string) {
    setRegelFouten((huidig) => ({ ...huidig, [id]: boodschap }));
  }

  function vergeetFout(id: number) {
    setRegelFouten((huidig) => {
      const rest = { ...huidig };
      delete rest[id];
      return rest;
    });
  }

  async function probeerOpnieuw(id: number) {
    vergeetFout(id);
    try {
      await opnieuwProberen.mutateAsync(id);
    } catch (err) {
      onthoudFout(
        id,
        `Doorzetten is niet gelukt. ${err instanceof Error ? err.message : 'Probeer het later opnieuw.'}`
      );
    }
  }

  async function koppelOpnieuw(id: number) {
    vergeetFout(id);
    try {
      await opnieuwKoppelen.mutateAsync(id);
    } catch (err) {
      onthoudFout(
        id,
        `Koppelen is niet gelukt. ${err instanceof Error ? err.message : 'Probeer het later opnieuw.'}`
      );
    }
  }

  // Zolang het overzicht laadt staat er een laadregel en niet een pagina met
  // vier lege blokken: "Alles doorgezet." bij een lijst die nog moet komen is
  // precies de geruststelling die dit scherm niet mag geven.
  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto py-8 text-sm text-gray-500">Overzicht laden…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="verlof-afsluiten-page" className="max-w-5xl mx-auto">
        {isError && (
          <p
            role="alert"
            className="mb-6 rounded-md border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#D64545]"
          >
            Het overzicht kon niet geladen worden. Probeer het later opnieuw.
          </p>
        )}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1E293B]">Verlof afsluiten</h1>
            <p className="mt-1 text-sm text-gray-500">
              Wat er vóór de maandafsluiting nog moet gebeuren.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label htmlFor="afsluiten-maand" className="text-sm text-[#64748B]">
              Maand
            </label>
            <input
              id="afsluiten-maand"
              type="month"
              value={maand}
              onChange={(e) => setMaand(e.target.value)}
              className="rounded-lg border border-[#E2E8F0] px-3 py-1.5 text-sm text-[#1E293B]"
            />
            <span
              data-testid="verlof-afsluiten-totaal"
              className="inline-block rounded-full bg-[#54949E]/10 px-3 py-1 text-sm font-medium text-[#54949E]"
            >
              {totaalOpenstaand} openstaand
            </span>
          </div>
        </div>

        {maandIsAfgesloten && (
          <p className="mb-6 rounded-md border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 text-sm text-[#1E293B]">
            Deze maand is afgesloten. Alleen HR kan hier nog corrigeren.
          </p>
        )}

        <div className="mb-6 rounded-lg border border-[#E2E8F0] bg-white p-4">
          <p className="text-base font-semibold text-[#1E293B]">
            Verlof koppelen aan de loonadministratie
          </p>
          <p className="mt-1 text-sm text-[#64748B]">
            Zoekt voor bestaand goedgekeurd verlof het kenmerk op, zodat het later te corrigeren is.
            Dit leest alleen; er verandert niets in de loonadministratie.
          </p>
          {koppelactie && (
            <p className="mt-2 text-xs text-[#64748B]">
              {`Laatst uitgevoerd: ${formatVolledigeDatum(koppelactie.laatstUitgevoerdOp)} — ${koppelactie.gekoppeld} van ${koppelactie.totaal} gekoppeld.`}
            </p>
          )}
          <button
            type="button"
            onClick={() => koppelactieStarten.mutate(undefined)}
            disabled={koppelactieStarten.isPending}
            className="mt-3 rounded-md bg-[#2F5A7A] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            Verlof koppelen starten
          </button>
        </div>

        <AfsluitBlok
          titel="Wacht op de loonadministratie"
          ondertitel="Goedgekeurd, maar nog niet doorgezet."
          testhaak="afsluiten-blok-wacht"
          aantal={wacht.length}
        >
          {wacht.map((regel) => (
            <AandachtRij
              key={regel.id}
              regel={regel}
              reden={regel.syncError}
              fout={regelFouten[regel.id] ?? null}
              actie={
                <button
                  type="button"
                  onClick={() => void probeerOpnieuw(regel.id)}
                  className="shrink-0 rounded-md border border-[#E2E8F0] px-3 py-1 text-sm text-[#1E293B]"
                >
                  Opnieuw proberen
                </button>
              }
            />
          ))}
        </AfsluitBlok>

        <AfsluitBlok
          titel="Uren nog niet bevestigd"
          ondertitel="Worden de geschatte uren als er niets gebeurt."
          testhaak="afsluiten-blok-onbevestigd"
          aantal={onbevestigd.length}
        >
          {onbevestigd.map((regel) => (
            <AandachtRij
              key={regel.id}
              regel={regel}
              actie={
                <button
                  type="button"
                  aria-label={`Aanpassen — ${regel.employeeNaam}`}
                  onClick={() => setCorrectieDoel(regel)}
                  className="shrink-0 rounded-md border border-[#E2E8F0] px-3 py-1 text-sm text-[#1E293B]"
                >
                  Aanpassen
                </button>
              }
            />
          ))}
        </AfsluitBlok>

        <AfsluitBlok
          titel="Niet gekoppeld aan de loonadministratie"
          ondertitel="Corrigeren kan alleen met de hand in Nmbrs."
          testhaak="afsluiten-blok-ongekoppeld"
          aantal={ongekoppeld.length}
        >
          {ongekoppeld.map((regel) => (
            <AandachtRij
              key={regel.id}
              regel={regel}
              reden={regel.redenCode ? REDEN_TEKST[regel.redenCode] : null}
              fout={regelFouten[regel.id] ?? null}
              actie={
                // AC-5: bij de vier redenen zonder kans van slagen staat er
                // helemaal géén knop — ook geen uitgegrijsde, want die nodigt
                // uit tot proberen. De vlag komt van de server; deze pagina
                // leidt hem nooit af uit de reden-tekst.
                regel.redenIsOpnieuwTeProberen ? (
                  <button
                    type="button"
                    onClick={() => void koppelOpnieuw(regel.id)}
                    className="shrink-0 rounded-md border border-[#E2E8F0] px-3 py-1 text-sm text-[#1E293B]"
                  >
                    Opnieuw koppelen
                  </button>
                ) : undefined
              }
            />
          ))}
        </AfsluitBlok>

        <AfsluitBlok
          titel="Calamiteiten- en zorgverlof van deze maand"
          ondertitel="Verwerk deze uren zelf in de loonadministratie."
          testhaak="afsluiten-blok-calamiteiten"
          aantal={buiten.length}
          legeTekst="Deze maand is er geen calamiteiten- of zorgverlof goedgekeurd."
          vinkjeBijLeeg={false}
          uitleg="Dit verlof gaat met opzet niet naar de loonadministratie. Deze lijst is je overzicht voor de maandafsluiting."
          kolomkoppen={['Naam', 'Datum', 'Uren']}
        >
          {buiten.map((regel) => (
            <AandachtRij key={regel.id} regel={regel} toonMarkering={false} />
          ))}
        </AfsluitBlok>
      </div>

      {correctieDoel && (
        <LeaveCorrectionSheet
          isOpen
          onClose={() => setCorrectieDoel(null)}
          onSubmitted={() => setCorrectieDoel(null)}
          historyEntry={alsHistorieRegel(correctieDoel)}
          employeeName={correctieDoel.employeeNaam}
        />
      )}
    </Layout>
  );
}
